import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { format } from "date-fns";
import {
  CheckCircleIcon,
  TruckIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";


// Define order status options
type OrderStatus = "pending" | "shipped" | "delivered";

interface OrderItem {
  description: string;
  imageUrl: string;
  name: string;
  price: number;
  productId: string;
  quantity: number;
}

interface Order {
  id: string;
  amount_total: number;
  created: Timestamp;
  currency: string;
  items: OrderItem[];
  metadata: {
    cartItems: string;
  };
  orderType: "shop_order";
  sessionId?: string;
  shipping: {
    address: {
      city: string;
      country: string;
      postal_code: string;
      state: string;
      street: string;
    };
    email: string;
    name: string;
    phone: string;
  };
  status: "pending" | "shipped" | "delivered";
}

const OrderManagement: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [openOrderDetails, setOpenOrderDetails] = useState(false);
  const [openStatusUpdate, setOpenStatusUpdate] = useState(false);
  const [newStatus, setNewStatus] = useState<OrderStatus>("pending");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isUpdating, setIsUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilters, setStatusFilters] = useState<OrderStatus[]>([]);

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    // Apply search and filters to orders
    let result = [...orders];

    // Apply search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (order) =>
          order.shipping.email.toLowerCase().includes(term) ||
          (order.shipping.name &&
            order.shipping.name.toLowerCase().includes(term)) ||
          order.id.toLowerCase().includes(term) ||
          (order.shipping.address.city &&
            order.shipping.address.city.toLowerCase().includes(term)) ||
          (order.shipping.address.country &&
            order.shipping.address.country.toLowerCase().includes(term))
      );
    }

    // Apply status filters
    if (statusFilters.length > 0) {
      result = result.filter((order) => statusFilters.includes(order.status));
    }

    // Sort orders by creation time (most recent first)
    result.sort((a, b) => {
      const dateA = a.created.toDate();
      const dateB = b.created.toDate();
      return dateB.getTime() - dateA.getTime();
    });

    setFilteredOrders(result);
  }, [orders, searchTerm, statusFilters]);

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const ordersRef = collection(db, "orders");
      const querySnapshot = await getDocs(ordersRef);

      const ordersData: Order[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        ordersData.push({
          id: doc.id,
          amount_total: (data.amount_total || data.totalAmount || 0) / 100,
          created: data.created,
          currency: data.currency,
          items: data.items,
          metadata: data.metadata,
          orderType: data.orderType,
          sessionId: data.sessionId || data.stripeSessionId || null,
          shipping: data.shipping,
          status: data.status || "pending",
        });
      });

      setOrders(ordersData);
      setFilteredOrders(ordersData);
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError("Failed to load orders. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewOrderDetails = (order: Order) => {
    setSelectedOrder(order);
    setOpenOrderDetails(true);
  };

  const handleOpenStatusUpdate = (order: Order) => {
    setSelectedOrder(order);
    setNewStatus(order.status);
    setOpenStatusUpdate(true);
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setNewStatus(event.target.value as OrderStatus);
  };

  const handleUpdateStatus = async () => {
    if (!selectedOrder) return;

    try {
      setIsUpdating(true);
      const orderRef = doc(db, "orders", selectedOrder.id);
      await updateDoc(orderRef, {
        status: newStatus,
      });

      // Update local state
      setOrders(
        orders.map((order) =>
          order.id === selectedOrder.id
            ? { ...order, status: newStatus }
            : order
        )
      );

      setOpenStatusUpdate(false);
      setSelectedOrder(null);
    } catch (err) {
      console.error("Error updating order status:", err);
      setError("Failed to update order status. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusBadge = (status: OrderStatus) => {
    switch (status) {
      case "shipped":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <TruckIcon className="w-3 h-3 mr-1" />
            Shipped
          </span>
        );
      case "delivered":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="w-3 h-3 mr-1" />
            Delivered
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <ExclamationCircleIcon className="w-3 h-3 mr-1" />
            Pending
          </span>
        );
    }
  };

  const formatDate = (timestamp: Timestamp) => {
    const date = timestamp.toDate();
    return format(date, "MMM d, yyyy h:mm a");
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleStatusFilterChange = (status: OrderStatus) => {
    setStatusFilters((prev) => {
      if (prev.includes(status)) {
        return prev.filter((s) => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilters([]);
  };

  const renderOrderDetails = () => {
    if (!selectedOrder) return null;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">
              Order Information
            </h3>
            <div className="space-y-2">
              <p className="text-sm">
                <span className="font-medium">Date:</span>{" "}
                {formatDate(selectedOrder.created)}
              </p>
              <p className="text-sm">
                <span className="font-medium">Status:</span>{" "}
                {selectedOrder.status}
              </p>
              <p className="text-sm">
                <span className="font-medium">Total Amount:</span> $
                {selectedOrder.amount_total.toFixed(2)}
              </p>
              <p className="text-sm">
                <span className="font-medium">Currency:</span>{" "}
                {selectedOrder.currency.toUpperCase()}
              </p>
              <p className="text-sm">
                <span className="font-medium">Order Type:</span>{" "}
                {selectedOrder.orderType}
              </p>
              <p className="text-sm">
                <span className="font-medium">Session ID:</span>{" "}
                {selectedOrder.sessionId ? selectedOrder.sessionId.slice(0, 12) + "..." : "N/A"}
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">
              Customer Information
            </h3>
            <div className="space-y-2">
              <p className="text-sm">
                <span className="font-medium">Name:</span>{" "}
                {selectedOrder.shipping.name}
              </p>
              <p className="text-sm">
                <span className="font-medium">Email:</span>{" "}
                {selectedOrder.shipping.email}
              </p>
              <p className="text-sm">
                <span className="font-medium">Phone:</span>{" "}
                {selectedOrder.shipping.phone}
              </p>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-3">
            Shipping Address
          </h3>
          <div className="space-y-1">
            <p className="text-sm">{selectedOrder.shipping.name}</p>
            <p className="text-sm">{selectedOrder.shipping.address.street}</p>
            <p className="text-sm">
              {selectedOrder.shipping.address.city},{" "}
              {selectedOrder.shipping.address.state}{" "}
              {selectedOrder.shipping.address.postal_code}
            </p>
            <p className="text-sm">{selectedOrder.shipping.address.country}</p>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-3">
            Order Items
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Product
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Description
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Price
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Quantity
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Subtotal
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {selectedOrder.items.map((item) => (
                  <tr key={item.productId}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {item.imageUrl && (
                          <img
                            src={item.imageUrl}
                            alt={item.name}
                            className="w-10 h-10 mr-3 object-cover rounded"
                          />
                        )}
                        <span className="text-sm font-medium text-gray-900">
                          {item.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      ${(item.price / 100).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                      {item.quantity}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                      ${((item.price / 100) * item.quantity).toFixed(2)}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-right">
                    <span className="text-sm font-medium text-gray-900">
                      Total
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="text-sm font-medium text-gray-900">
                      ${selectedOrder.amount_total.toFixed(2)}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderFilterSection = () => (
    <div className={`mb-6 ${showFilters ? "block" : "hidden"}`}>
      <div className="bg-white p-4 rounded-lg shadow">
        <h3 className="text-base font-medium text-gray-900 mb-3">Filters</h3>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center space-x-2">
            <input
              id="filter-pending"
              type="checkbox"
              checked={statusFilters.includes("pending")}
              onChange={() => handleStatusFilterChange("pending")}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="filter-pending" className="text-sm text-gray-700">
              Pending
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <input
              id="filter-shipped"
              type="checkbox"
              checked={statusFilters.includes("shipped")}
              onChange={() => handleStatusFilterChange("shipped")}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="filter-shipped" className="text-sm text-gray-700">
              Shipped
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <input
              id="filter-delivered"
              type="checkbox"
              checked={statusFilters.includes("delivered")}
              onChange={() => handleStatusFilterChange("delivered")}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="filter-delivered" className="text-sm text-gray-700">
              Delivered
            </label>
          </div>
        </div>
        <button
          onClick={clearFilters}
          className="mt-3 text-sm text-blue-600 hover:text-blue-800"
        >
          Clear Filters
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center p-12">
        <ArrowPathIcon className="h-8 w-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">{error}</div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Order Management
        </h1>
        <p className="text-gray-600">View and manage all customer orders</p>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 flex justify-between flex-wrap gap-4 border-b border-gray-200">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search by email, name, or location"
              value={searchTerm}
              onChange={handleSearchChange}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-64"
            />
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FunnelIcon className="h-5 w-5 mr-2 text-gray-500" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
            <button
              onClick={() => fetchOrders()}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <ArrowPathIcon className="h-5 w-5 mr-2 text-gray-500" />
              Refresh Orders
            </button>
          </div>
        </div>

        {renderFilterSection()}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Order ID
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Date
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Customer
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Shipping Address
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Items
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Total
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Status
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center">
                    {orders.length === 0
                      ? "No orders found"
                      : "No orders match your filters"}
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <tr key={order.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {order.id.slice(0, 8)}...
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {formatDate(order.created)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {order.shipping.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {order.shipping.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {order.shipping.address.city},{" "}
                            {order.shipping.address.state}
                          </div>
                          <div className="text-sm text-gray-500">
                            {order.shipping.address.country}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {order.items.length} items
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      ${order.amount_total.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(order.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewOrderDetails(order)}
                          className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleOpenStatusUpdate(order)}
                          className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          Update Status
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Order Details Modal */}
      {openOrderDetails && selectedOrder && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-xl font-semibold text-gray-900">
                Order Details - {selectedOrder.id}
              </h3>
              <button
                onClick={() => setOpenOrderDetails(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">{renderOrderDetails()}</div>
            <div className="flex justify-end gap-3 p-4 border-t">
              <button
                onClick={() => setOpenOrderDetails(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setOpenOrderDetails(false);
                  handleOpenStatusUpdate(selectedOrder);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Update Status
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {openStatusUpdate && selectedOrder && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-xl font-semibold text-gray-900">
                Update Order Status
              </h3>
              <button
                onClick={() => setOpenStatusUpdate(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <label
                  htmlFor="status-select"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Status
                </label>
                <select
                  id="status-select"
                  value={newStatus}
                  onChange={handleStatusChange}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  <option value="pending">Pending</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t">
              <button
                onClick={() => setOpenStatusUpdate(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateStatus}
                disabled={isUpdating}
                className={`px-4 py-2 bg-blue-600 text-white rounded-md ${
                  isUpdating
                    ? "opacity-70 cursor-not-allowed"
                    : "hover:bg-blue-700"
                }`}
              >
                {isUpdating ? (
                  <span className="flex items-center">
                    <ArrowPathIcon className="animate-spin -ml-1 mr-2 h-4 w-4" />
                    Updating...
                  </span>
                ) : (
                  "Update"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderManagement;
